"use client";

import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { handleEmailNotificationsClick } from "../handlers";
import { useUserDataContext } from "../user-data-context";
import styles from "./card.module.css";
import Switcher, { SwitcherLoading } from "./switcher";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import UpgradeAgencyModal from "./modals/upgrade-agency-modal";
import AgencyApplicationModal from "./modals/agency-application-modal";
import { PendingAgencyApplication, PendingAgencyCardData } from "../types";
import { useScrollLock } from "@/hooks/use-scroll-lock";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

export default function AgencyCard() {
  const { state, updateField } = useUserDataContext();
  const t = useTranslations("MyAccount");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [applicationModalData, setApplicationModalData] =
    useState<PendingAgencyCardData | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const agencyApplicationModalRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const handledRef = useRef<string | null>(null);

  const applicationIdFromUrl = searchParams.get("applicationId");

  useEffect(() => {
    if (!applicationIdFromUrl) return;
    if (handledRef.current === applicationIdFromUrl) return;
    handledRef.current = applicationIdFromUrl;
    setApplicationModalData({
      request_id: applicationIdFromUrl,
    } as PendingAgencyCardData);

    // remove param (clean URL)
    router.replace(pathname, { scroll: false });
  }, [applicationIdFromUrl]);

  useScrollLock(isFormModalOpen);
  useScrollLock(applicationModalData !== null);

  return (
    <>
      <div className={styles.cardWrapper}>
        <div className={styles.detailsWrapper}>
          <div className={styles.switcherRow}>
            <p>{t("agencyCta")}</p>
          </div>
        </div>
        {state.applications?.length > 0 && (
          <div className={styles.applicationsWrapper}>
            <div className={styles.applicationsHeader}>
              <span>{t("yourApplications")}</span>
              <span className={styles.applicationsCount}>
                {state.applications?.length ?? 0}
              </span>
            </div>

            <div className={styles.applicationsList}>
              {/* Application item */}
              {state.applications?.map((application) => {
                return (
                  <div
                    key={application.request_id}
                    className={styles.applicationItem}
                    onClick={(e) => {
                      e.preventDefault();
                      setApplicationModalData(application);
                    }}
                  >
                    <div className={styles.applicationMain}>
                      <span className={styles.applicationName}>
                        {application.agency_name}
                      </span>
                      <span
                        className={`${styles.statusBadge} ${
                          styles[application.status]
                        }`}
                      >
                        {t(`statuses.${application.status}`)}
                      </span>
                    </div>
                    <span className={styles.applicationDate}>
                      {t("submitted")} · {application.created_at}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <button
          className={`${styles.button} ${styles.agencyUpgrade}`}
          aria-label={t("ariaButtonUpgrade")}
          onClick={(e) => {
            e.preventDefault();
            setIsFormModalOpen(true);
          }}
        >
          {t("agencyButtonCta")}
        </button>
      </div>
      {isFormModalOpen && (
        <UpgradeAgencyModal
          isOpen={isFormModalOpen}
          dialogRef={dialogRef}
          closeModal={() => {
            setIsFormModalOpen(false);
          }}
        />
      )}
      {applicationModalData !== null && (
        <AgencyApplicationModal
          applicationId={applicationModalData.request_id}
          dialogRef={agencyApplicationModalRef}
          isOpen={applicationModalData !== null}
          onCloseAction={() => setApplicationModalData(null)}
        />
      )}
    </>
  );
}

export function AgencyCardLoading() {
  return (
    <div className={styles.cardWrapper}>
      <div className={styles.detailsWrapper}>
        <div className={styles.switcherRow} style={{ gap: "16px" }}>
          <div style={{ width: "100%" }}>
            <p
              className={styles.isLoading}
              style={{ width: "100%", marginBottom: "4px" }}
            ></p>
            <p
              className={styles.isLoading}
              style={{ width: "100%", marginBottom: "4px" }}
            ></p>
            <p className={styles.isLoading} style={{ width: "60%" }}></p>
          </div>
        </div>
      </div>
      <button className={`${styles.button} ${styles.isLoading}`}></button>
    </div>
  );
}
